import React from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 150px;
  margin-bottom: 30px;
  
  ${mediaQueries.large} {
    padding: 50px 100px;
  }
  
  ${mediaQueries.medium} {
    padding: 40px 40px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 40px;
  text-align: right;
  line-height: 1.1;
`;

const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 0px;
`;

const PageSubtitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 30px;
`;

const Description = styled.p`
  font-size: 24px;
  line-height: 1.5;
  max-width: 800px;
  margin-bottom: 40px;
`;

const BoldDescription = styled(Description)`
  font-weight: bold;
`;

const HighlightedText = styled.span`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 700;
`;

const MainImage = styled.img`
  width: 100%;
  max-width: 900px;
  height: auto;
  border-radius: 16px;
  margin: 20px auto 0;
  display: block;
`;

const IASection = styled.section`
  background-color: white;
  max-width: 900px;
  color: #0A1A2F;
  padding: 60px 60px;
  border-radius: 20px;
  margin: 0 auto 60px;
  border: 12px solid #0A1A2F;
  
  ${mediaQueries.large} {
    max-width: 80%;
    padding: 40px;
  }
  
  ${mediaQueries.medium} {
    max-width: 90%;
    padding: 30px;
    border-width: 8px;
  }
  
  ${mediaQueries.small} {
    max-width: 95%;
    padding: 20px;
    border-width: 6px;
  }
`;

const IASectionContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const IATitle = styled.h2`
  font-size: 36px;
  margin-bottom: 20px;
  text-align: center;
  display: block;
`;

const IADescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 30px;
  text-align: left;
`;

const TechSection = styled.section`
  padding: 60px 20px;
  text-align: center;
  margin-bottom: 60px;
`;

const TechTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 10px;
`;

const TechSubtitle = styled.h3`
  font-size: 24px;
  margin-bottom: 50px;
  font-weight: var(--font-weight-medium);
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin: 0 auto;
  max-width: 900px;

  ${mediaQueries.medium} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TechItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface TechIconProps {
  scale?: number;
}

const TechIcon = styled.img<TechIconProps>`
  width: ${props => props.scale ? `${120 * (props.scale/100)}px` : '120px'};
  height: 120px;
  object-fit: contain;
  margin-bottom: 15px;
`;

const TechName = styled.span`
  font-size: 14px;
  font-weight: var(--font-weight-medium);
`;

const TestimonialsSection = styled.section`
  padding: 60px 20px;
  text-align: center;
  margin-bottom: 60px;
`;

const TestimonialsTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 10px;
`;

const TestimonialsSubtitle = styled.h3`
  font-size: 24px;
  margin-bottom: 50px;
  color: ${({theme}) => theme.colors.secondary};
  font-weight: var(--font-weight-medium);
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin: 0 auto;
  max-width: 900px;

  ${mediaQueries.large} {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  text-align: left;
  padding: 20px;
`;

const TestimonialText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
  font-style: italic;
`;

const TestimonialPerson = styled.div`
  display: flex;
  align-items: center;
`;

const TestimonialAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 15px;
`;

const TestimonialInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestimonialName = styled.span`
  font-weight: var(--font-weight-medium);
  font-size: 16px;
`;

const TestimonialRole = styled.span`
  font-size: 14px;
  color: ${({theme}) => theme.colors.darkGray};
`;

const ContactSection = styled.section`
  padding: 40px 20px 60px;
  text-align: center;
`;

const ContactTitle = styled.h2`
  font-size: 36px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 20px;
`;

const ContactInfo = styled.div`
  font-size: 16px;
  margin-bottom: 30px;
`;

const ContactButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 30px;
  border-radius: 32px;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  display: inline-block;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const Metier: React.FC = () => {
  return (
    <PageContainer>
      <SEO 
        title="Notre expertise technique" 
        description="Thinkfab est une société spécialisée en développement de logiciels SaaS. Découvrez notre expertise technique et nos solutions d'innovation."
        canonical={`${window.location.origin}/metier`}
        image="/images/metier/wdd-metier-team.png"
      />
      <Header backgroundColor="transparent" textColor="#DD3114" />
      
      <MainSection>
        <HeroSection>
          <TitleContainer>
            <PageTitle>
              <HighlightedText>We build projects.</HighlightedText>
            </PageTitle>
            <PageSubtitle>Nous réalisons vos logiciels.</PageSubtitle>
          </TitleContainer>
          
          <Description>
            Nous sommes une société de conseil et de création de logiciel.
            Notre équipe est spécialisée dans le développement
            de logiciels en mode SaaS (web et mobile).
          </Description>
          
          <Description style={{ fontWeight: 'bold' }}>
            Nous anticipons les évolutions technologiques grâce à une veille constante sur
            <HighlightedText> l'innovation</HighlightedText>, pour proposer à nos clients de nouveaux usages et services à
            forte valeur ajoutée.
          </Description>
          
          <MainImage src="/images/metier/wdd-metier-team.png" alt="Équipe de développement" />
        </HeroSection>
        
        <IASection>
          <IASectionContent>
            <IATitle>
              <HighlightedText>We use AI.</HighlightedText> Intelligence non artificielle.
            </IATitle>
            
            <IADescription>
              L'intelligence artificielle est un outil puissant que nous mettons au
              service de votre innovation. <strong>Nous sélectionnons les meilleurs modèles de langage</strong> (Claude d'Anthropic, Mistral et autres solutions open source) pour répondre
              globalement à vos problématiques et pour vous accompagner dans des
              environnements innovants.<br />
              Grâce à l'utilisation de LLM locaux, <strong>vos données restent
              sous votre contrôle, en toute sécurité.</strong><br />
              Notre équipe expérimente ces technologies au quotidien : nous l'appliquons déjà en interne
              pour la revue de code et l'optimisation de nos processus, et
              même la génération de ce site web.<br />
              <strong style={{ color: '#DD3114' }}>Notre philosophie est simple : l'IA doit servir
              l'intelligence humaine, pas la remplacer.</strong>
            </IADescription>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '30px' }}>
              <img src="/images/metier/anthropic.svg" alt="Anthropic " style={{ height: '60px' }} />
              <img src="/images/metier/mistral.svg" alt="Mistral" style={{ height: '60px' }} />
            </div>
          </IASectionContent>
        </IASection>
        
        <TechSection>
          <TechTitle>
            <HighlightedText>We like tech.</HighlightedText>
          </TechTitle>
          <TechSubtitle>Toujours une techno d'avance.</TechSubtitle>
          
          <TechGrid>
            <TechItem>
              <TechIcon src="/images/metier/logo-symfony.svg" alt="Symfony" scale={110} />
              <TechName>Symfony</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-apiplatform.svg" alt="API Platform" scale={95} />
              <TechName>API Platform</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-reactnative.svg" alt="React Native" scale={100} />
              <TechName>React Native</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-docker.svg" alt="Docker" scale={80} />
              <TechName>Docker</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-postgresql.svg" alt="PostgreSQL" scale={75} />
              <TechName>PostgreSQL</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images//metier/logo-expo.svg" alt="Expo" scale={70} />
              <TechName>Expo</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-elasticsearch.svg" alt="Elastic Search" scale={90} />
              <TechName>Elastic</TechName>
            </TechItem>
            <TechItem>
              <TechIcon src="/images/metier/logo-kubernetes.svg" alt="Kubernetes" scale={90} />
              <TechName>Kubernetes</TechName>
            </TechItem>
          </TechGrid>
        </TechSection>
        

      </MainSection>
      
      <Footer 
        pageContext="Page Métier"
        heading={
          <>
            Vous avez un projet ? 
            <span role="img" aria-label="waving hand">👋</span>
          </>
        }
        subheading="Discutons de votre projet."
        buttonText="Envoyez un message"
        buttonLink="/contact"
      />
    </PageContainer>
  );
};

export default Metier;